import axios, { AxiosError } from 'axios'
import type { JobSummary, JobDetail, VATChartEntry } from './types'

const BASE = 'https://vat-recovery.finkraftai.com'

// Keep in sync with server-side limit (nginx client_max_body_size / FastAPI limit).
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024 // 50 MB
export const MAX_FILES_PER_BATCH = 20

export const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 15_000, // 15s — enough for cold start, not long enough to hang the UI
})

// ── Interceptor: surface errors clearly, don't let AxiosError bubble as runtime crash
api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<{ detail?: string; message?: string }>) => {
    // Timeout
    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      err.message =
        'Backend is not responding. Check your connection or try a smaller file.'
      return Promise.reject(err)
    }

    // No response at all — network / CORS / DNS
    if (!err.response) {
      err.message = 'Network error — could not reach the server.'
      return Promise.reject(err)
    }

    // Prefer the server's own error message when available
    const serverMsg =
      err.response.data?.detail ??
      err.response.data?.message ??
      null

    switch (err.response.status) {
      case 400:
        err.message = serverMsg ?? 'Invalid request.'
        break
      case 401:
        err.message = serverMsg ?? 'You are not authorized. Please sign in again.'
        break
      case 403:
        err.message = serverMsg ?? 'You do not have permission to perform this action.'
        break
      case 404:
        err.message = serverMsg ?? 'Resource not found.'
        break
      case 413:
        err.message =
          serverMsg ??
          `Upload too large. Maximum allowed is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB per request. Please split your files and try again.`
        break
      case 415:
        err.message = serverMsg ?? 'Unsupported file type.'
        break
      case 422:
        err.message = serverMsg ?? 'The server could not process the provided data.'
        break
      case 429:
        err.message = serverMsg ?? 'Too many requests. Please wait a moment and try again.'
        break
      case 500:
      case 502:
      case 503:
      case 504:
        err.message = serverMsg ?? 'Server error. Please try again shortly.'
        break
      default:
        err.message = serverMsg ?? `Request failed (HTTP ${err.response.status}).`
    }

    return Promise.reject(err)
  },
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Validates a batch before it ever hits the network.
 * Throws a friendly Error if the batch is empty, has too many files,
 * contains an individually oversized file, or totals over the per-request cap.
 */
export function validateUploadBatch(files: File[]): void {
  if (!files || files.length === 0) {
    throw new Error('No files selected.')
  }

  if (files.length > MAX_FILES_PER_BATCH) {
    throw new Error(
      `Too many files in one upload (${files.length}). Maximum is ${MAX_FILES_PER_BATCH} per batch.`,
    )
  }

  const oversized = files.filter((f) => f.size > MAX_UPLOAD_BYTES)
  if (oversized.length > 0) {
    const names = oversized.map((f) => `"${f.name}" (${formatMB(f.size)})`).join(', ')
    throw new Error(
      `The following file(s) exceed the ${formatMB(MAX_UPLOAD_BYTES)} limit: ${names}`,
    )
  }

  const total = files.reduce((sum, f) => sum + f.size, 0)
  if (total > MAX_UPLOAD_BYTES) {
    throw new Error(
      `Total upload size ${formatMB(total)} exceeds the ${formatMB(MAX_UPLOAD_BYTES)} per-request limit. Please upload the files in smaller batches.`,
    )
  }
}

/**
 * Splits a list of files into batches whose total size stays under MAX_UPLOAD_BYTES.
 * Any single file larger than the cap is rejected (can't be chunked at this layer).
 */
export function chunkFilesBySize(
  files: File[],
  maxBytes: number = MAX_UPLOAD_BYTES,
): File[][] {
  const oversized = files.find((f) => f.size > maxBytes)
  if (oversized) {
    throw new Error(
      `"${oversized.name}" is ${formatMB(oversized.size)}, which exceeds the ${formatMB(maxBytes)} per-file limit.`,
    )
  }

  const batches: File[][] = []
  let current: File[] = []
  let currentSize = 0

  for (const file of files) {
    if (currentSize + file.size > maxBytes && current.length > 0) {
      batches.push(current)
      current = []
      currentSize = 0
    }
    current.push(file)
    currentSize += file.size
  }
  if (current.length > 0) batches.push(current)
  return batches
}

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export interface UploadOptions {
  rateMode?: 'reduced' | 'standard'
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}

/**
 * Upload a batch of files. Accepts either the legacy `rateMode` string or
 * an `UploadOptions` object for progress / cancellation support.
 *
 * NOTE: Do NOT set the Content-Type header manually. Axios/the browser will set
 * `multipart/form-data; boundary=...` correctly only when left alone.
 */
export async function uploadFiles(
  files: File[],
  rateMode?: 'reduced' | 'standard',
): Promise<JobSummary[]>
export async function uploadFiles(
  files: File[],
  options?: UploadOptions,
): Promise<JobSummary[]>
export async function uploadFiles(
  files: File[],
  arg?: 'reduced' | 'standard' | UploadOptions,
): Promise<JobSummary[]> {
  const options: UploadOptions =
    typeof arg === 'string' ? { rateMode: arg } : arg ?? {}

  const { rateMode = 'reduced', onProgress, signal } = options

  validateUploadBatch(files)

  const form = new FormData()
  files.forEach((f) => form.append('files', f, f.name))

  const { data } = await api.post<JobSummary[]>(
    `/jobs/upload?rate_mode=${rateMode}`,
    form,
    {
      timeout: 120_000, // uploads can be large; 2 min is comfortable
      signal,
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      },
    },
  )
  return data
}

/**
 * Convenience wrapper: upload an arbitrarily large set of files by splitting
 * into size-capped batches automatically. Progress is reported across the
 * whole set, not per batch.
 */
export async function uploadFilesChunked(
  files: File[],
  options: UploadOptions = {},
): Promise<JobSummary[]> {
  const batches = chunkFilesBySize(files)
  const totalBytes = files.reduce((s, f) => s + f.size, 0)
  let uploadedBytes = 0
  const results: JobSummary[] = []

  for (const batch of batches) {
    const batchBytes = batch.reduce((s, f) => s + f.size, 0)

    const summaries = await uploadFiles(batch, {
      ...options,
      onProgress: (pct) => {
        if (options.onProgress) {
          const overall = ((uploadedBytes + (batchBytes * pct) / 100) / totalBytes) * 100
          options.onProgress(Math.round(overall))
        }
      },
    })

    uploadedBytes += batchBytes
    results.push(...summaries)
  }

  return results
}

export async function listJobs(): Promise<JobSummary[]> {
  const { data } = await api.get<JobSummary[]>('/jobs')
  return data
}

export async function getJob(id: string): Promise<JobDetail> {
  const { data } = await api.get<JobDetail>(`/jobs/${id}`)
  return data
}

export async function deleteJob(id: string): Promise<void> {
  await api.delete(`/jobs/${id}`)
}

export function exportUrl(id: string): string {
  return `${BASE}/api/jobs/${id}/export`
}

export async function fetchVATChart(): Promise<VATChartEntry[]> {
  const { data } = await api.get<VATChartEntry[]>('/vat-chart')
  return data
}

export async function checkHealth(): Promise<boolean> {
  try {
    await api.get('/health', { timeout: 3000 })
    return true
  } catch {
    return false
  }
}

export async function reprocessJob(
  jobId: string,
  rateMode: 'reduced' | 'standard',
): Promise<void> {
  await api.post(`/jobs/${jobId}/reprocess?rate_mode=${rateMode}`)
}